// =============================================================================
// PLAN 2D GENERATOR — éditeur Konva pièces salle de bain PMR
//
// JJ 2026-05-20 — refonte :
//   1. Catalogue équipements depuis API /api/metier/catalogue/produits
//      (catégorie "equipement_pmr") — Stéphanie gère depuis Hub Catalogue.
//      Fallback hardcoded si API indisponible (résilience prod).
//   2. Dimensions modifiables AVANT ajout (douche 90×90 vs douche 150×90
//      sur le même plan).
//   3. Murs visibles avec épaisseur + porte positionnée selon le mur saisi
//      en amont (Plan2DAutoBlock.mur_porte).
//   4. Équipements en CENTIMÈTRES (source de vérité) — conversion px
//      via scale au rendu — restent lisibles à n'importe quel zoom.
//   5. Toast confirmation save (parent gère dans DALForm.jsx).
//   6. Pièces non rectangulaires Option A (CC-17 V1.1) : toggle
//      rectangulaire / L-shape avec 2 sous-rectangles.
//   7. Save : TOUJOURS construire plan_2d_data (mode manuel inclus) —
//      fix bug 350×220 vs 246×160 (silent skip si pas d'IA initiale).
// =============================================================================

import { useState, useRef, useEffect, useCallback } from "react";
import { Stage, Layer, Rect, Line, Text, Circle, Group, Arrow } from "react-konva";
import {
  Save, Download, RotateCcw, ZoomIn, ZoomOut, Plus, Trash2, Grid,
  Box, Shapes,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

const API = process.env.REACT_APP_BACKEND_URL || "";
const DEFAULT_SCALE = 50; // 1m = 50px

// ─── Styles charte OmniHub (dual-mode via tokens var(--oh-*)) — chrome UI ────
// NB : les couleurs de rendu Konva (murs, sol, porte, équipements) restent des
// valeurs de dessin « plan d'architecte » (comme un PDF sur fond blanc) et ne
// sont PAS des couleurs de chrome UI — laissées telles quelles volontairement.
const surfaceStyle = { background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)" };
const inputStyle = { background: "var(--oh-bg-elevated)", color: "var(--oh-text)", borderColor: "var(--oh-border)" };
const btnPrimary = { background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" };
const btnSecondary = { background: "transparent", border: "1px solid var(--oh-accent)", color: "var(--oh-accent-text)" };
const btnDiscreet = { background: "transparent", border: "1px solid var(--oh-border)", color: "var(--oh-text-secondary)" };
const btnDanger = { background: "transparent", border: "1px solid var(--oh-error-border)", color: "var(--oh-error-text)" };
const menuActive = { background: "var(--oh-accent-bg)", color: "var(--oh-accent-text)", borderColor: "var(--oh-accent)" };
const menuIdle = { background: "var(--oh-bg-surface)", color: "var(--oh-text-secondary)", borderColor: "transparent" };
const txtSec = { color: "var(--oh-text-secondary)" };
const txtMuted = { color: "var(--oh-text-muted)" };

// Fallback hardcoded — utilisé si l'API catalogue n'est pas dispo (prod
// resilience). Doit rester synchro avec seed_pmr_equipment_2026_05_20.py.
const FALLBACK_EQUIPMENTS = [
  { key: "baignoire",    label: "Baignoire",     width_cm: 170, height_cm: 70, color: "#60A5FA" },
  { key: "douche",       label: "Douche PMR",    width_cm:  90, height_cm: 90, color: "#34D399" },
  { key: "wc",           label: "WC",            width_cm:  40, height_cm: 60, color: "#F472B6" },
  { key: "lavabo",       label: "Lavabo",        width_cm:  60, height_cm: 45, color: "#A78BFA" },
  { key: "lave_linge",   label: "Lave-linge",    width_cm:  60, height_cm: 60, color: "#94A3B8" },
  { key: "meuble_vasque",label: "Meuble vasque", width_cm:  80, height_cm: 50, color: "#FB923C" },
  { key: "barre_appui",  label: "Barre d'appui", width_cm:  60, height_cm:  5, color: "#EF4444" },
  { key: "siege_douche", label: "Siège douche",  width_cm:  40, height_cm: 40, color: "#22D3EE" },
];

// Couleur stable pour un équipement inconnu (catalogue libre Stéphanie)
function _colorForKey(key) {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) % 360;
  return `hsl(${h}, 65%, 60%)`;
}

// Composant Équipement — dims stockées en cm, rendues en px via scale
function Equipment({ equipment, isSelected, onSelect, onDrag, onDelete, scale, catalog }) {
  const cfg = catalog.find(c => c.key === equipment.type) || catalog[0] || FALLBACK_EQUIPMENTS[0];
  // Source de vérité : width_cm / height_cm sur l'équipement ; fallback catalogue
  const widthCm = equipment.width_cm ?? equipment.width ?? cfg.width_cm;
  const heightCm = equipment.height_cm ?? equipment.height ?? cfg.height_cm;
  const widthPx = (widthCm / 100) * scale;
  const heightPx = (heightCm / 100) * scale;
  const isAI = equipment.ai_confidence != null && !equipment._verified;
  // Label adaptatif : si trop petit on cache, sinon fontSize proportionnel
  const minDim = Math.min(widthPx, heightPx);
  const fontSize = Math.max(8, Math.min(14, minDim * 0.22));
  const showLabel = minDim > 18;

  return (
    <Group
      x={equipment.x}
      y={equipment.y}
      draggable
      onClick={() => onSelect(equipment.id)}
      onTap={() => onSelect(equipment.id)}
      onDragEnd={(e) => onDrag(equipment.id, e.target.x(), e.target.y())}
      rotation={equipment.rotation || 0}
    >
      <Rect
        width={widthPx}
        height={heightPx}
        fill={cfg.color || _colorForKey(equipment.type)}
        opacity={0.78}
        cornerRadius={3}
        stroke={isSelected ? "#FBBF24" : (isAI ? "#F59E0B" : "#1E293B")}
        strokeWidth={isSelected ? 3 : (isAI ? 2 : 1)}
        dash={isAI ? [6, 4] : undefined}
      />
      {showLabel && (
        <Text
          text={cfg.label}
          width={widthPx}
          height={heightPx}
          align="center"
          verticalAlign="middle"
          fontSize={fontSize}
          fill="#0F172A"
          fontStyle="bold"
        />
      )}
      {showLabel && (
        <Text
          text={`${Math.round(widthCm)}×${Math.round(heightCm)}`}
          x={2}
          y={heightPx - fontSize - 2}
          fontSize={Math.max(7, fontSize * 0.7)}
          fill="#1E293B"
          opacity={0.65}
        />
      )}
      {isAI && (
        <Text text="IA" x={2} y={-12} fontSize={9} fill="#F59E0B" fontStyle="bold" />
      )}
      {isSelected && (
        <Circle
          x={widthPx}
          y={0}
          radius={10}
          fill="#EF4444"
          onClick={(e) => { e.cancelBubble = true; onDelete(equipment.id); }}
        />
      )}
    </Group>
  );
}

function Dimension({ x1, y1, x2, y2, label, offset = 20 }) {
  const isHorizontal = Math.abs(y1 - y2) < Math.abs(x1 - x2);
  const midX = (x1 + x2) / 2;
  const midY = (y1 + y2) / 2;
  return (
    <Group>
      <Arrow
        points={[x1, y1 + (isHorizontal ? offset : 0), x2, y2 + (isHorizontal ? offset : 0)]}
        stroke="#9CA3AF"
        strokeWidth={1}
        pointerLength={6}
        pointerWidth={6}
        pointerAtBeginning
        pointerAtEnding
      />
      <Text
        x={midX - 20}
        y={midY + (isHorizontal ? offset + 5 : -15)}
        text={label}
        fontSize={11}
        fill="#9CA3AF"
        fontStyle="bold"
      />
    </Group>
  );
}

// Rendu d'un rectangle de pièce (murs avec épaisseur visible)
function RoomRect({ x, y, width, height, name, scale, porte }) {
  const WALL_STROKE = 8;
  return (
    <Group>
      {/* Sol */}
      <Rect x={x} y={y} width={width} height={height} fill="#F8FAFC" />
      {/* 4 murs comme rectangles épais (pour mieux visualiser épaisseur) */}
      <Rect x={x} y={y} width={width} height={WALL_STROKE} fill="#334155" />
      <Rect x={x} y={y + height - WALL_STROKE} width={width} height={WALL_STROKE} fill="#334155" />
      <Rect x={x} y={y} width={WALL_STROKE} height={height} fill="#334155" />
      <Rect x={x + width - WALL_STROKE} y={y} width={WALL_STROKE} height={height} fill="#334155" />
      {/* Nom de pièce */}
      {name && (
        <Text x={x + WALL_STROKE + 4} y={y + WALL_STROKE + 4} text={name} fontSize={12} fill="#1E293B" fontStyle="bold" />
      )}
      {/* Porte (si porte fournie pour CE rectangle) */}
      {porte && porte.mur && (() => {
        const largeurPx = ((porte.largeur_cm || 80) / 100) * scale;
        const posPx = porte.position_cm != null ? (porte.position_cm / 100) * scale : null;
        let px, py, pw, ph;
        if (porte.mur === "nord") {
          px = posPx != null ? x + posPx : x + (width - largeurPx) / 2;
          py = y - 2;
          pw = largeurPx; ph = WALL_STROKE + 4;
        } else if (porte.mur === "sud") {
          px = posPx != null ? x + posPx : x + (width - largeurPx) / 2;
          py = y + height - WALL_STROKE - 2;
          pw = largeurPx; ph = WALL_STROKE + 4;
        } else if (porte.mur === "est") {
          px = x + width - WALL_STROKE - 2;
          py = posPx != null ? y + posPx : y + (height - largeurPx) / 2;
          pw = WALL_STROKE + 4; ph = largeurPx;
        } else { // ouest
          px = x - 2;
          py = posPx != null ? y + posPx : y + (height - largeurPx) / 2;
          pw = WALL_STROKE + 4; ph = largeurPx;
        }
        return (
          <Group>
            <Rect x={px} y={py} width={pw} height={ph} fill="#FBBF24" stroke="#92400E" strokeWidth={1} />
            <Text
              x={px}
              y={py + ph + 2}
              text={`Porte ${porte.largeur_cm || 80}cm`}
              fontSize={9}
              fill="#92400E"
            />
          </Group>
        );
      })()}
    </Group>
  );
}

export default function Plan2DGenerator({
  roomData = {},
  initialData = null,
  tenantId = "tef",
  onSave,
  onExport,
  readOnly = false,
}) {
  const stageRef = useRef(null);
  const containerRef = useRef(null);

  // ── Dimensions pièce (cm) ──
  const [roomDimensions, setRoomDimensions] = useState({
    width: initialData?.dims?.longueur_cm || roomData.width || 350,
    height: initialData?.dims?.largeur_cm || roomData.height || 220,
    name: initialData?.name || roomData.name || "Salle de bain",
  });

  // ── Forme pièce : "rectangular" | "composite" (L shape via 2 rects) ──
  const [roomShape, setRoomShape] = useState(
    initialData?.room_shape === "composite" ? "composite" : "rectangular"
  );
  // Sous-rectangle additionnel pour forme L (largeur, longueur, offset depuis coin nord-ouest du principal)
  const [subRect, setSubRect] = useState(
    initialData?.sub_rectangles?.[0] || { width_cm: 150, height_cm: 100, offset_x_cm: 0, offset_y_cm: 220 }
  );

  // ── Porte (récupérée de l'IA si dispo, sinon mur nord par défaut) ──
  const [porte, setPorte] = useState(
    initialData?.porte || { mur: "nord", largeur_cm: 80, position_cm: null }
  );

  // ── Catalogue équipements depuis API (avec fallback) ──
  const [catalog, setCatalog] = useState(FALLBACK_EQUIPMENTS);
  const [catalogLoading, setCatalogLoading] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setCatalogLoading(true);
      try {
        const token = localStorage.getItem("session_token");
        const headers = {
          "X-Tenant-ID": tenantId || "tef",
          ...(token && { Authorization: `Bearer ${token}` }),
        };
        const res = await fetch(
          `${API}/api/metier/catalogue/produits?categorie=equipement_pmr`,
          { headers, credentials: "include" }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const items = await res.json();
        if (cancelled) return;
        // Normaliser au format interne {key, label, width_cm, height_cm, color}
        const normalized = (items || [])
          .filter(p => p.actif !== false && p.largeur_cm && p.longueur_cm)
          .map(p => ({
            key: p.reference || p.id,
            label: p.designation || p.nom || p.reference,
            // Convention catalogue : longueur_cm = grand axe, largeur_cm = petit axe
            // Plan2D : width = grand axe horizontal, height = petit axe vertical
            width_cm: Number(p.longueur_cm) || 60,
            height_cm: Number(p.largeur_cm) || 60,
            color: p.color_hex || _colorForKey(p.reference || p.id || ""),
          }));
        if (normalized.length > 0) setCatalog(normalized);
      } catch (e) {
        // Garde fallback hardcoded — pas bloquant pour l'utilisateur
        console.warn("[Plan2D] catalogue API échec, fallback hardcoded:", e.message);
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId]);

  // ── Équipements placés ──
  // Normalisation : les équipements peuvent venir de l'IA avec width/height en PX
  // (Plan2DData.equipments) ou de la prochaine génération avec width_cm/height_cm.
  // On stocke en cm en interne pour rester scalable au zoom.
  const _normalizeIncomingEquipment = useCallback((eq, fallbackScale = DEFAULT_SCALE) => {
    if (eq.width_cm != null && eq.height_cm != null) return eq;
    // Cas legacy : width/height en px → convertir
    if (eq.width != null && eq.height != null) {
      return {
        ...eq,
        width_cm: (eq.width / fallbackScale) * 100,
        height_cm: (eq.height / fallbackScale) * 100,
      };
    }
    return eq;
  }, []);

  const [equipments, setEquipments] = useState(() =>
    (initialData?.equipments || roomData.equipments || []).map(eq => _normalizeIncomingEquipment(eq))
  );
  const [selectedEquipment, setSelectedEquipment] = useState(null);

  // ── Échelle / zoom ──
  const [scale, setScale] = useState(DEFAULT_SCALE);
  const [stageSize, setStageSize] = useState({ width: 600, height: 400 });

  // ── État d'ajout : type sélectionné + dims libres modifiables ──
  const [equipmentToAdd, setEquipmentToAdd] = useState("");
  const [customWidth, setCustomWidth] = useState("");
  const [customHeight, setCustomHeight] = useState("");

  // Sync taille canvas avec container
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        // Hauteur adaptative selon la pièce (plus de place pour forme L)
        const baseHeight = roomShape === "composite" ? 560 : 420;
        setStageSize({ width: Math.max(width, 400), height: baseHeight });
      }
    };
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, [roomShape]);

  const cmToPx = useCallback((cm) => (cm / 100) * scale, [scale]);

  // Bounding box global (pour la forme L)
  const totalWidthCm = roomShape === "composite"
    ? Math.max(roomDimensions.width, subRect.offset_x_cm + subRect.width_cm)
    : roomDimensions.width;
  const totalHeightCm = roomShape === "composite"
    ? Math.max(roomDimensions.height, subRect.offset_y_cm + subRect.height_cm)
    : roomDimensions.height;

  const offsetX = (stageSize.width - cmToPx(totalWidthCm)) / 2;
  const offsetY = (stageSize.height - cmToPx(totalHeightCm)) / 2;

  // Dims par défaut quand on sélectionne un équipement (depuis catalogue)
  useEffect(() => {
    if (!equipmentToAdd) {
      setCustomWidth("");
      setCustomHeight("");
      return;
    }
    const cfg = catalog.find(c => c.key === equipmentToAdd);
    if (cfg) {
      setCustomWidth(String(cfg.width_cm));
      setCustomHeight(String(cfg.height_cm));
    }
  }, [equipmentToAdd, catalog]);

  // Ajout équipement avec dims libres
  const addEquipment = () => {
    if (!equipmentToAdd) return;
    const cfg = catalog.find(c => c.key === equipmentToAdd);
    const w = parseFloat(customWidth) || cfg?.width_cm || 60;
    const h = parseFloat(customHeight) || cfg?.height_cm || 60;
    const newEquipment = {
      id: `eq_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      type: equipmentToAdd,
      x: offsetX + 30,
      y: offsetY + 30,
      width_cm: w,
      height_cm: h,
      rotation: 0,
    };
    setEquipments([...equipments, newEquipment]);
    // Reset sélection pour le prochain ajout
    setEquipmentToAdd("");
    setCustomWidth("");
    setCustomHeight("");
  };

  const moveEquipment = (id, x, y) => {
    setEquipments(equipments.map(eq => eq.id === id ? { ...eq, x, y } : eq));
  };

  const deleteEquipment = (id) => {
    setEquipments(equipments.filter(eq => eq.id !== id));
    setSelectedEquipment(null);
  };

  const rotateSelected = () => {
    if (!selectedEquipment) return;
    setEquipments(equipments.map(eq =>
      eq.id === selectedEquipment
        ? { ...eq, rotation: ((eq.rotation || 0) + 90) % 360 }
        : eq
    ));
  };

  const resetPlan = () => {
    setEquipments([]);
    setSelectedEquipment(null);
  };

  const exportToPNG = () => {
    if (!stageRef.current) return;
    const uri = stageRef.current.toDataURL({ pixelRatio: 2 });
    const link = document.createElement("a");
    link.download = `Plan_${roomDimensions.name.replace(/\s/g, "_")}.png`;
    link.href = uri;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    if (onExport) onExport(uri);
  };

  // Save : TOUJOURS construire plan_2d_data — fix bug "manual mode silent skip"
  const handleSave = () => {
    const plan_2d_data = {
      source: initialData ? "ia_then_edited" : "manual",
      room_shape: roomShape === "composite" ? "composite" : "rectangular",
      dims: {
        longueur_cm: parseInt(roomDimensions.width, 10) || 0,
        largeur_cm: parseInt(roomDimensions.height, 10) || 0,
      },
      porte: {
        mur: porte.mur || "nord",
        largeur_cm: parseInt(porte.largeur_cm, 10) || 80,
        position_cm: porte.position_cm != null ? parseInt(porte.position_cm, 10) : null,
      },
      equipments: equipments.map(eq => {
        const cfg = catalog.find(c => c.key === eq.type) || catalog[0] || FALLBACK_EQUIPMENTS[0];
        const wcm = eq.width_cm ?? cfg.width_cm;
        const hcm = eq.height_cm ?? cfg.height_cm;
        return {
          id: eq.id,
          type: eq.type,
          x: eq.x,
          y: eq.y,
          // Backend Plan2DEquipment attend width/height en px (legacy)
          width: (wcm / 100) * scale,
          height: (hcm / 100) * scale,
          rotation: eq.rotation || 0,
          ai_confidence: eq.ai_confidence ?? null,
        };
      }),
      source_photos: initialData?.source_photos || [],
    };
    // Sub-rectangles pour forme L
    if (roomShape === "composite") {
      plan_2d_data.sub_rectangles = [{
        width_cm: parseInt(subRect.width_cm, 10) || 0,
        height_cm: parseInt(subRect.height_cm, 10) || 0,
        offset_x_cm: parseInt(subRect.offset_x_cm, 10) || 0,
        offset_y_cm: parseInt(subRect.offset_y_cm, 10) || 0,
      }];
    }
    const data = {
      dimensions: roomDimensions,
      equipments,
      scale,
      plan_2d_data,
    };
    if (onSave) onSave(data);
  };

  return (
    <div className="space-y-4" data-testid="plan-2d-generator">
      {/* === Forme + dimensions === */}
      {!readOnly && (
        <div className="rounded-lg p-4 space-y-4 border" style={surfaceStyle}>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h4 className="font-medium flex items-center gap-2" style={{ color: "var(--oh-text)" }}>
              <Grid className="w-4 h-4" style={{ color: "var(--oh-accent-text)" }} />
              Forme et dimensions
            </h4>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setRoomShape("rectangular")}
                className="px-3 py-1 text-xs rounded flex items-center gap-1 border"
                style={roomShape === "rectangular" ? menuActive : menuIdle}
                data-testid="room-shape-rect"
              >
                <Box className="w-3 h-3" /> Rectangulaire
              </button>
              <button
                onClick={() => setRoomShape("composite")}
                className="px-3 py-1 text-xs rounded flex items-center gap-1 border"
                style={roomShape === "composite" ? menuActive : menuIdle}
                data-testid="room-shape-l"
              >
                <Shapes className="w-3 h-3" /> Forme L (2 rectangles)
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs" style={txtSec}>Nom de la pièce</Label>
              <Input
                value={roomDimensions.name}
                onChange={(e) => setRoomDimensions({ ...roomDimensions, name: e.target.value })}
                className="border" style={inputStyle}
                placeholder="Ex: Salle de bain"
              />
            </div>
            <div>
              <Label className="text-xs" style={txtSec}>Longueur (cm) — grand axe</Label>
              <Input
                type="number"
                value={roomDimensions.width}
                onChange={(e) => setRoomDimensions({ ...roomDimensions, width: parseInt(e.target.value) || 0 })}
                className="border" style={inputStyle}
                min={50} max={2000}
                data-testid="plan2d-room-longueur"
              />
            </div>
            <div>
              <Label className="text-xs" style={txtSec}>Largeur (cm) — petit axe</Label>
              <Input
                type="number"
                value={roomDimensions.height}
                onChange={(e) => setRoomDimensions({ ...roomDimensions, height: parseInt(e.target.value) || 0 })}
                className="border" style={inputStyle}
                min={50} max={2000}
                data-testid="plan2d-room-largeur"
              />
            </div>
          </div>

          {/* Porte */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 border-t" style={{ borderColor: "var(--oh-border)" }}>
            <div>
              <Label className="text-xs" style={txtSec}>Mur de la porte</Label>
              <Select value={porte.mur} onValueChange={(v) => setPorte({ ...porte, mur: v })}>
                <SelectTrigger className="border" style={inputStyle}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nord">Nord (haut)</SelectItem>
                  <SelectItem value="sud">Sud (bas)</SelectItem>
                  <SelectItem value="est">Est (droite)</SelectItem>
                  <SelectItem value="ouest">Ouest (gauche)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs" style={txtSec}>Largeur porte (cm)</Label>
              <Input
                type="number"
                value={porte.largeur_cm}
                onChange={(e) => setPorte({ ...porte, largeur_cm: parseInt(e.target.value) || 80 })}
                className="border" style={inputStyle}
                min={60} max={200}
              />
            </div>
            <div>
              <Label className="text-xs" style={txtSec}>Position porte (cm, optionnel)</Label>
              <Input
                type="number"
                value={porte.position_cm ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setPorte({ ...porte, position_cm: v === "" ? null : (parseInt(v) || 0) });
                }}
                className="border" style={inputStyle}
                placeholder="Centré si vide"
                min={0}
              />
            </div>
          </div>

          {/* Sub-rectangle pour forme L */}
          {roomShape === "composite" && (
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 pt-2 border-t" style={{ borderColor: "var(--oh-border)" }}
                 data-testid="plan2d-subrect-fields">
              <div className="sm:col-span-4">
                <p className="text-xs" style={{ color: "var(--oh-info-text)" }}>
                  Forme L — 2e rectangle : configure ses dimensions et son
                  offset depuis le coin haut-gauche du principal.
                </p>
              </div>
              <div>
                <Label className="text-xs" style={txtSec}>2e rect — Longueur (cm)</Label>
                <Input type="number" value={subRect.width_cm} min={50} max={2000}
                  onChange={(e) => setSubRect({ ...subRect, width_cm: parseInt(e.target.value) || 0 })}
                  className="border" style={inputStyle} />
              </div>
              <div>
                <Label className="text-xs" style={txtSec}>2e rect — Largeur (cm)</Label>
                <Input type="number" value={subRect.height_cm} min={50} max={2000}
                  onChange={(e) => setSubRect({ ...subRect, height_cm: parseInt(e.target.value) || 0 })}
                  className="border" style={inputStyle} />
              </div>
              <div>
                <Label className="text-xs" style={txtSec}>Offset X (cm)</Label>
                <Input type="number" value={subRect.offset_x_cm} min={0} max={2000}
                  onChange={(e) => setSubRect({ ...subRect, offset_x_cm: parseInt(e.target.value) || 0 })}
                  className="border" style={inputStyle} />
              </div>
              <div>
                <Label className="text-xs" style={txtSec}>Offset Y (cm)</Label>
                <Input type="number" value={subRect.offset_y_cm} min={0} max={2000}
                  onChange={(e) => setSubRect({ ...subRect, offset_y_cm: parseInt(e.target.value) || 0 })}
                  className="border" style={inputStyle} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* === Ajout équipement avec dimensions libres === */}
      {!readOnly && (
        <div className="rounded-lg p-3 space-y-2 border" style={surfaceStyle}>
          <div className="flex items-center justify-between">
            <Label className="text-xs font-medium" style={{ color: "var(--oh-text-secondary)" }}>
              Ajouter un équipement
              {catalogLoading && <span className="ml-2" style={txtMuted}>(chargement…)</span>}
            </Label>
            <span className="text-[10px]" style={txtMuted}>
              {catalog.length} équipements ({catalog === FALLBACK_EQUIPMENTS ? "fallback" : "catalogue"})
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr_auto] gap-2 items-end">
            <div>
              <Label className="text-[10px]" style={txtSec}>Équipement</Label>
              <Select value={equipmentToAdd} onValueChange={setEquipmentToAdd}>
                <SelectTrigger className="border" style={inputStyle} data-testid="plan2d-equipment-select">
                  <SelectValue placeholder="Sélectionner…" />
                </SelectTrigger>
                <SelectContent>
                  {catalog.map(c => (
                    <SelectItem key={c.key} value={c.key}>
                      {c.label} ({c.width_cm}×{c.height_cm} cm)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px]" style={txtSec}>Longueur (cm)</Label>
              <Input
                type="number"
                value={customWidth}
                onChange={(e) => setCustomWidth(e.target.value)}
                placeholder="-"
                className="border" style={inputStyle}
                disabled={!equipmentToAdd}
                data-testid="plan2d-custom-width"
              />
            </div>
            <div>
              <Label className="text-[10px]" style={txtSec}>Largeur (cm)</Label>
              <Input
                type="number"
                value={customHeight}
                onChange={(e) => setCustomHeight(e.target.value)}
                placeholder="-"
                className="border" style={inputStyle}
                disabled={!equipmentToAdd}
                data-testid="plan2d-custom-height"
              />
            </div>
            <Button
              onClick={addEquipment}
              disabled={!equipmentToAdd}
              style={btnPrimary}
              data-testid="plan2d-add-button"
            >
              <Plus className="w-4 h-4 mr-1" />
              Ajouter
            </Button>
          </div>
        </div>
      )}

      {/* === Barre actions === */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg p-3 border" style={surfaceStyle}>
        {!readOnly && selectedEquipment && (
          <>
            <Button size="sm" variant="outline" onClick={rotateSelected} style={btnDiscreet}>
              <RotateCcw className="w-4 h-4 mr-1" /> Pivoter
            </Button>
            <Button size="sm" variant="outline" onClick={() => deleteEquipment(selectedEquipment)} style={btnDanger}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </>
        )}
        <div className="flex items-center gap-1 ml-auto">
          <Button size="sm" variant="ghost" onClick={() => setScale(Math.max(30, scale - 10))}>
            <ZoomOut className="w-4 h-4" />
          </Button>
          <span className="text-xs w-12 text-center" style={txtSec}>{scale}px/m</span>
          <Button size="sm" variant="ghost" onClick={() => setScale(Math.min(150, scale + 10))}>
            <ZoomIn className="w-4 h-4" />
          </Button>
        </div>
        {!readOnly && (
          <>
            <Button size="sm" variant="outline" onClick={resetPlan} style={btnDiscreet}>
              <RotateCcw className="w-4 h-4 mr-1" /> Reset
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleSave}
              style={btnSecondary}
              data-testid="plan2d-save-button"
            >
              <Save className="w-4 h-4 mr-1" /> Sauvegarder
            </Button>
          </>
        )}
        <Button size="sm" variant="outline" onClick={exportToPNG} style={btnSecondary}>
          <Download className="w-4 h-4 mr-1" /> Exporter PNG
        </Button>
      </div>

      {/* === Canvas Konva === */}
      <div
        ref={containerRef}
        className="rounded-lg overflow-hidden border-2"
        style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)" }}
        data-testid="plan-2d-canvas"
      >
        <Stage
          ref={stageRef}
          width={stageSize.width}
          height={stageSize.height}
          onClick={(e) => {
            if (e.target === e.target.getStage()) {
              setSelectedEquipment(null);
            }
          }}
        >
          <Layer>
            {/* Grille de fond */}
            {Array.from({ length: Math.ceil(stageSize.width / 20) }).map((_, i) => (
              <Line key={`vgrid_${i}`} points={[i * 20, 0, i * 20, stageSize.height]} stroke="#E5E7EB" strokeWidth={0.5} />
            ))}
            {Array.from({ length: Math.ceil(stageSize.height / 20) }).map((_, i) => (
              <Line key={`hgrid_${i}`} points={[0, i * 20, stageSize.width, i * 20]} stroke="#E5E7EB" strokeWidth={0.5} />
            ))}

            {/* Pièce principale */}
            <RoomRect
              x={offsetX}
              y={offsetY}
              width={cmToPx(roomDimensions.width)}
              height={cmToPx(roomDimensions.height)}
              name={roomDimensions.name}
              scale={scale}
              porte={roomShape === "rectangular" ? porte : null}
            />

            {/* 2e rectangle pour forme L */}
            {roomShape === "composite" && (
              <RoomRect
                x={offsetX + cmToPx(subRect.offset_x_cm)}
                y={offsetY + cmToPx(subRect.offset_y_cm)}
                width={cmToPx(subRect.width_cm)}
                height={cmToPx(subRect.height_cm)}
                name="2e rectangle"
                scale={scale}
                porte={porte}  /* porte rendue sur le 2e quand forme L */
              />
            )}

            {/* Cotations sur le principal */}
            <Dimension
              x1={offsetX}
              y1={offsetY + cmToPx(roomDimensions.height)}
              x2={offsetX + cmToPx(roomDimensions.width)}
              y2={offsetY + cmToPx(roomDimensions.height)}
              label={`${roomDimensions.width} cm`}
              offset={25}
            />
            <Dimension
              x1={offsetX + cmToPx(roomDimensions.width)}
              y1={offsetY}
              x2={offsetX + cmToPx(roomDimensions.width)}
              y2={offsetY + cmToPx(roomDimensions.height)}
              label={`${roomDimensions.height} cm`}
              offset={25}
            />

            {/* Équipements */}
            {equipments.map((eq) => (
              <Equipment
                key={eq.id}
                equipment={eq}
                isSelected={selectedEquipment === eq.id}
                onSelect={setSelectedEquipment}
                onDrag={moveEquipment}
                onDelete={deleteEquipment}
                scale={scale}
                catalog={catalog}
              />
            ))}
          </Layer>
        </Stage>
      </div>

      {/* Légende */}
      {equipments.length > 0 && (
        <div className="text-xs" style={txtSec}>
          {equipments.length} équipement{equipments.length > 1 ? "s" : ""} —
          {" "}
          {equipments.map(eq => {
            const cfg = catalog.find(c => c.key === eq.type);
            const w = Math.round(eq.width_cm ?? cfg?.width_cm ?? 0);
            const h = Math.round(eq.height_cm ?? cfg?.height_cm ?? 0);
            return `${cfg?.label || eq.type} ${w}×${h}`;
          }).join(" · ")}
        </div>
      )}
    </div>
  );
}
